import type { AuthMethod } from '@lit-protocol/types'
import { generateRunnerSessionData } from '@polaris/core/mpc'
import { runnerSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import { useAtom } from 'jotai'
import { useCallback, useMemo } from 'react'
import { useLitClient } from './use-lit-client'

/**
 * Hook for managing runner session signatures with Lit Protocol.
 *
 * @returns An object containing:
 *   - `initiateRunnerSessionSigs`: Function to create new runner session signatures
 *   - `runnerSessionKeyData`: Current runner session key data
 */
export function useRunnerSessionSigs() {
  const { litClient } = useLitClient()
  const [runnerSessionData, setRunnerSessionData] = useAtom(
    runnerSessionDataAtom,
  )

  /**
   * Retrieves the current runner session key data from atom state.
   *
   * @returns Session key data object if all required data is available, undefined otherwise
   */
  const runnerSessionKeyData = useMemo(() => {
    if (!runnerSessionData) {
      return undefined
    }

    return {
      sessionKeyPair: runnerSessionData.sessionKeyPair,
      walletSig: runnerSessionData.authSig,
      expiration: runnerSessionData.expiration,
    }
  }, [runnerSessionData])

  /**
   * Initiates a new runner session by generating session signatures for a PKP.
   *
   * This function:
   * 1. Clears any existing default session key data from localStorage
   * 2. Generates a new expiration date for the session
   * 3. Connects to the Lit client
   * 4. Requests session signatures from Lit with runner permissions
   * 5. Extracts the session key data from localStorage (set by Lit client)
   * 6. Stores the session data in atoms for use throughout the app
   * 7. Cleans up the default session key data from localStorage
   *
   * @param pkpPublicKey - The public key of the PKP to create session signatures for
   * @param authMethod - The authentication method to use (e.g., wallet signature)
   * @param guestMode - Whether to use guest mode resource ability requests
   * @returns An object containing the session signatures, session key data, and expiration
   * @throws Error if session key data cannot be generated
   */
  const initiateRunnerSessionSigs = useCallback(
    async (pkpPublicKey: string, authMethod: AuthMethod, guestMode = false) => {
      const sessionData = await generateRunnerSessionData(
        litClient,
        window.localStorage,
        pkpPublicKey,
        authMethod,
        guestMode,
      )

      setRunnerSessionData({
        authSig: sessionData.walletSig,
        sessionKeyPair: sessionData.sessionKey,
        expiration: sessionData.expiration,
      })

      return sessionData
    },
    [litClient, setRunnerSessionData],
  )

  return {
    initiateRunnerSessionSigs,
    runnerSessionKeyData,
  }
}
